import { useEffect, useState } from 'react';
import { UserProfile, ProjectGlobalData, EmailLog, Report } from '../types';
import { supabase } from '../services/supabaseClient';
import { loadProjectById, fetchProjectEmailLogs } from '../services/cloudService';
import { fetchLinkedChildren } from '../services/projectLinkService';
import { companyFilter } from '../services/companyScope';

interface UseProjectHubDataProps {
    globalData: ProjectGlobalData;
    currentUser?: UserProfile | null;
    /** Nur-Ansicht: es wird ausschliesslich gelesen, auch beim Mutterprojekt. */
    isReadOnly?: boolean;
}

export const useProjectHubData = ({ globalData, currentUser, isReadOnly = false }: UseProjectHubDataProps) => {
    const [availableUsers, setAvailableUsers] = useState<UserProfile[]>([]);
    const [linkedChildren, setLinkedChildren] = useState<any[]>([]);
    const [linkedParent, setLinkedParent] = useState<Report | null>(null);
    const [projectEmailLogs, setProjectEmailLogs] = useState<EmailLog[]>([]);
    const [showSentReports, setShowSentReports] = useState(false);
    const [sentReportsLoadFailed, setSentReportsLoadFailed] = useState(false);
    const [dbCurrentNr, setDbCurrentNr] = useState<number | undefined>(undefined);
    const [dbLastExportedNr, setDbLastExportedNr] = useState<number | undefined>(undefined);
    const [isLegacyCorrected, setIsLegacyCorrected] = useState<boolean | null>(null);

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                let query = supabase.from('profiles').select('*');
                const filter = companyFilter(currentUser);
                if (filter) query = query.eq(filter.column, filter.value);
                const { data } = await query;
                if (data) setAvailableUsers(data);
            } catch (e) { console.error("Error fetching users:", e); }
        };
        fetchUsers();
    }, [currentUser?.company_id, currentUser?.company_name]);

    useEffect(() => {
        const fetchChildren = async () => {
            if (!globalData.projectId || globalData.projectId === 'temp-id') return;
            try {
                // Ueber die Spalte aus Nr. 71 (Index), nicht mehr ueber eine
                // `contains(data, …)`-Suche im Blob — der ist seit dem
                // Vorfall 6290 nicht mehr die Wahrheit der Verknuepfung.
                const data = await fetchLinkedChildren<any>(globalData.projectId, 'id, name, data, project_number');
                setLinkedChildren(data);
            } catch (e) { console.error(e); }
        };
        fetchChildren();
    }, [globalData.projectId]);

    useEffect(() => {
        const fetchParent = async () => {
            const config = globalData.linkedProjectConfig;
            if (config && config.role === 'child' && config.linkedProjectId) {
                // Reines Anzeige-Laden: im Lesemodus ohne Bild-Selbstheilung,
                // damit fremde Projektzeilen unberuehrt bleiben.
                try { const parent = await loadProjectById(config.linkedProjectId, undefined, { skipMigration: isReadOnly }); if (parent && parent.data) setLinkedParent(parent.data); } catch (e) { console.error(e); }
            } else { setLinkedParent(null); }
        };
        fetchParent();
    }, [globalData.linkedProjectConfig, isReadOnly]);

    useEffect(() => {
        const fetchDbState = async () => {
            if (!globalData.projectId || globalData.projectId === 'temp-id') return;
            try {
                const { data } = await supabase.from('projects')
                    .select('current_inspection_number, last_exported_inspection_number, is_legacy_data_corrected')
                    .eq('id', globalData.projectId).maybeSingle();
                if (data) { setDbCurrentNr(data.current_inspection_number); setDbLastExportedNr(data.last_exported_inspection_number); setIsLegacyCorrected(data.is_legacy_data_corrected); }
            } catch (e) { console.warn(e); }
        };
        fetchDbState();
    }, [globalData.projectId]);

    const handleOpenSentReports = async () => {
        if (!globalData.projectId) return;
        // Serverseitig aufs Projekt gefiltert. Bis Oktober 2026 kamen hier alle
        // Sendeberichte aller Projekte und wurden erst im Browser gefiltert —
        // gekappt auf die neuesten 1000, aeltere Projekte verloren so ihren
        // Verlauf (6254: zwei Eintraege uebrig).
        try {
            setProjectEmailLogs(await fetchProjectEmailLogs(globalData.projectId));
            setSentReportsLoadFailed(false);
        } catch (e) {
            console.error('Sendeverlauf konnte nicht geladen werden:', e);
            setProjectEmailLogs([]);
            setSentReportsLoadFailed(true);
        }
        setShowSentReports(true);
    };

    return {
        availableUsers,
        linkedChildren,
        linkedParent, setLinkedParent,
        projectEmailLogs,
        sentReportsLoadFailed,
        showSentReports, setShowSentReports,
        dbCurrentNr, setDbCurrentNr,
        dbLastExportedNr,
        isLegacyCorrected, setIsLegacyCorrected,
        handleOpenSentReports,
    };
};
